import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import type { AppealReading, ReadingLevel } from "../contract"
import { lakh, shortDate } from "../answer/format"
import { BeforeTheNotice } from "./BeforeTheNotice"
import { Code } from "./Code"
import { PaywallDoor, useIsPro } from "../ds/gate"
import { CreditNote, LockedRailItem, PreviewDoor } from "./Gated"
import { AddedBanner, AttachButton, ReadingComposer, type Hydrate } from "./Hydrate"

const inr = new Intl.NumberFormat("en-IN")
const rupees = (n: number) => `₹${inr.format(Math.round(n))}`

const SECTIONS = [
  { n: "01", title: "What the order does not deal with" },
  { n: "02", title: "What needs more" },
  { n: "03", title: "Appeal, rectify, or pay" },
  { n: "04", title: "What Nira does not predict" },
  { n: "05", title: "What to do, by when" },
]
/** Past the preview: what each locked section holds, said in the overlay its rail item opens. */
const LOCKED_LINE: Record<string, string> = {
  "03": "Appeal, rectify or pay, each worked out: the pre-deposit, the reduced-penalty window and the forum, with the working.",
  "04": "What the order alone cannot tell, said plainly, so nothing is read as a predicted result.",
  "05": "What to do and by when: the appeal date, the days left, each step, and the grounds of appeal drafted.",
}
const WITH_CASE_FILE = "With your reply and documents, Nira reads which of your contentions the order does not deal with, how the merits stand on your documents, and whether the hearing point stands on its own."
const GROUND: Record<string, string> = {
  natural_justice: "natural justice", excess_demand: "excess demand", wrong_section: "section for the period",
  time_limit: "time limit", non_speaking: "non-speaking order",
}
const NEED: Record<string, { word: string; tone: string }> = {
  ready: { word: "ready now", tone: "helps" }, partly: { word: "partly", tone: "mixed" }, needs: { word: "needs a document", tone: "mixed" },
}
const OUTCOME: Record<string, string> = { confirmed: "confirmed", partly: "partly confirmed", dropped: "dropped" }

/** NA-Appeal from the order alone: dates, deposit, forum and the points in the order's own text. */
/** The day the reader received the order: the deadline runs from it when the order prints none. */
function ReceivedOn({ onDate, busy, prompt }: { onDate: (iso: string) => void; busy: boolean; prompt: string }) {
  const [value, setValue] = useState("")
  return (
    <form className="nrd-received" onSubmit={(e) => { e.preventDefault(); if (value) onDate(value) }}>
      <label htmlFor="nrd-received">{prompt}</label>
      <input id="nrd-received" type="date" value={value} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setValue(e.target.value)} />
      <button type="submit" className="ncf-btn primary" disabled={!value || busy}>{busy ? "Working it out…" : "Work out the deadline"}</button>
    </form>
  )
}

export function AppealPreview({ reading: r, level = "full", hydrate, caseLink, connected, onReceived, busy = false }: {
  reading: AppealReading; level?: ReadingLevel; hydrate?: Hydrate; caseLink: string; connected?: string; onReceived?: (iso: string) => void; busy?: boolean
}) {
  // After the free one: the glance, the findings, 01 and 02's heads; nothing past them was sent.
  const gated = level === "preview"
  // Drafting the grounds of appeal is Pro's (and still to come): Pro readers see no door, only that it is coming.
  const pro = useIsPro()
  const [changing, setChanging] = useState(false)
  const [on, setOn] = useState("01")
  useEffect(() => {
    const onScroll = () => {
      let current = SECTIONS[0].n
      for (const s of SECTIONS) {
        const el = document.getElementById(`nrd-${s.n}`)
        if (el && el.getBoundingClientRect().top <= 140) current = s.n
      }
      setOn(current)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])
  const h = r.header
  const d = r.deadlines
  const late = d.days_left !== null && d.days_left < 0
  return (
    <div className="nrd-page">
      <nav className="nrd-rail" aria-label="Appeal or not">
        <p className="ncf-eyebrow">Appeal or not</p>
        {SECTIONS.map((s) => gated && s.n > "02" ? (
          <LockedRailItem key={s.n} n={s.n} title={s.title} line={LOCKED_LINE[s.n]} metric="nira.appeal_reading" source="rail_lock" />
        ) : (
          <a key={s.n} href={`#nrd-${s.n}`} className={on === s.n ? "on" : ""} aria-current={on === s.n ? "true" : undefined}>
            <span className="mono">{s.n}</span>{s.title}
          </a>
        ))}
        <p className="nrd-rail-note">{gated ? "The dates, the deposit, the forum and 01 are worked out here. The rest of this order is read in Nira Pro." : "The reading with your documents, and the grounds drafted, are in Nira Pro."}</p>
      </nav>

      <main className="nrd-main">
        {hydrate ? <AddedBanner hydrate={hydrate} /> : null}
        {connected ? (
          <p className="nrd-connected">
            <b>Connected to your workspace:</b> {connected}. The notice and the facts you confirmed carried over. <Link to={caseLink}>Open it</Link>
          </p>
        ) : null}
        <header className="nrd-head">
          <p className="ncf-eyebrow">
            {h.appellate ? "Appeal to the Tribunal or not" : "Appeal or not"} · read from the dates and the order's own text, not from a predicted result
          </p>
          <h1>{r.lead.line}</h1>
          {r.lead.sub ? <p className="nrd-sub">{r.lead.sub}</p> : null}
          {onReceived && (!d.appeal_by || changing) ? (
            <ReceivedOn onDate={onReceived} busy={busy} prompt={d.appeal_by ? "The day you received the order" : "When did you receive the order?"} />
          ) : onReceived && !h.communicated_on ? (
            <button type="button" className="nrd-change" onClick={() => setChanging(true)}>Received it on another day? Change the date</button>
          ) : null}
          {r.next_step ? (
            <div className="nrd-next">
              <div>
                <b>Next: {r.next_step.title.toLowerCase()}</b><span>{r.next_step.line}</span>
              </div>
              {pro ? null : <PaywallDoor className="ncf-btn primary npro-btn" metric="nira.appeal_reading" trigger={9} source="grounds">Draft the grounds</PaywallDoor>}
            </div>
          ) : null}
          <div className="nrd-chips">
            {h.order_number ? <span className="nrd-chip mono" title={h.order_number}>{h.order_number}</span> : null}
            {h.communicated_on ? <span className="nrd-chip">{h.communicated_from_reader ? "received" : "communicated"} {shortDate(h.communicated_on)}</span> : h.order_date ? <span className="nrd-chip">order {shortDate(h.order_date)}</span> : null}
            {h.section ? <span className="nrd-chip">s.{h.section}{h.years.length ? ` · FY ${h.years.join(", ")}` : ""}</span> : null}
            {h.tax ? <span className="nrd-chip mono">{rupees(h.tax)} confirmed</span> : null}
            {h.penalty ? <span className="nrd-chip mono">penalty {rupees(h.penalty)}</span> : null}
          </div>
        </header>

        <div className="nrd-glance">
          <a href={gated ? "#nrd-locked" : "#nrd-05"}><span className="k">Appeal by</span><span className="v">{d.appeal_by ? shortDate(d.appeal_by) : "Not read"}</span><span className="s">{d.basis ?? "3 months from communication, s.107(1) · 1 month more with cause"}</span></a>
          <a href={gated ? "#nrd-locked" : "#nrd-03"}><span className="k">Pre-deposit</span><span className="v mono">{rupees(d.pre_deposit.amount)}</span><span className="s">{d.deposit_basis ?? "10% of tax in dispute, s.107(6) · capped at ₹20 crore"}</span></a>
          <a href={gated ? "#nrd-locked" : "#nrd-03"}><span className="k">Forum</span><span className="v">{d.forum.name}</span><span className="s">{h.appellate ? `next: ${d.forum.next}` : `${h.authority ? `from ${h.authority} · ` : ""}GSTAT next`}</span></a>
          <a href="#nrd-01"><span className="k">Points to raise</span><span className="v">{r.points.length} from the order's text</span><span className="s">{r.points.map((p) => GROUND[p.ground_type] ?? p.ground_type).join(", ") || "none found by code"}</span></a>
        </div>

        {r.findings.length ? (
          <ul className="nrd-algs nrd-decided" aria-label="What the order decided">
            {r.findings.map((f) => (
              <li key={f.id} className={f.outcome === "dropped" ? "quiet" : ""}>
                <span className="mono nrd-id">{f.id}</span>
                <span className="nrd-alg-t">
                  <b>{f.title}</b>
                  <span>{[OUTCOME[f.outcome], f.years.length ? `FY ${f.years.join(", ")}` : "", f.para ? `para ${f.para}` : ""].filter(Boolean).join(" · ")}</span>
                </span>
                <span className="mono nrd-amt">{f.outcome === "dropped" ? "—" : f.tax ? rupees(f.tax) : "—"}</span>
              </li>
            ))}
          </ul>
        ) : null}

        <section className="nrd-sec" id="nrd-01" aria-labelledby="nrd-01-h">
          <h2 id="nrd-01-h">01 · What the order does not deal with</h2>
          {r.points.length ? (
            <>
              <p className="nrd-read">
                {r.points.length === 1 ? "One point" : `${r.points.length} points`} a code check finds in the order's own text. Each is a ground an appellate authority would look at before the merits.
              </p>
              <div className="nrd-rows">
                {r.points.map((p) => (
                  <Code
                    key={p.kind + p.text}
                    line={<><b>{p.text}</b>{p.source ? <span className="nrd-src"> “{p.source}”</span> : null}</>}
                    working={p.working}
                    chip={GROUND[p.ground_type] ?? p.ground_type}
                    tone="mixed"
                  />
                ))}
              </div>
            </>
          ) : (
            <p className="nrd-read">The order's own text gives no point a code check can find. Whether your contentions were dealt with needs your reply.</p>
          )}
        </section>

        <section className="nrd-sec" id="nrd-02" aria-labelledby="nrd-02-h">
          <h2 id="nrd-02-h">02 · What the order alone can tell, and what needs more</h2>
          <div className="nrd-rows">
            {r.needs_more.map((n) => (
              <div className="nrd-row nrd-need" key={n.what}>
                <div className="nrd-need-l"><b>{n.what}</b>{n.detail ? <> · {n.detail}</> : null}{n.add ? <span className="nrd-add"> · {hydrate ? <AttachButton label={n.add.charAt(0).toUpperCase() + n.add.slice(1)} what={n.add} hydrate={hydrate} badge={<span className="npro-badge sm">Pro</span>} /> : <PaywallDoor className="npro-link" source="appeal_row" metric="nira.appeal_reading" trigger={8}>{n.add}</PaywallDoor>}</span> : null}</div>
                <span className={`nrd-chip ${NEED[n.status].tone}`}>{NEED[n.status].word}</span>
              </div>
            ))}
          </div>
          {gated ? (
            <PreviewDoor door="Read the appeal with your workspace" title="The appeal, read with your workspace" line={WITH_CASE_FILE} metric="nira.appeal_reading" source="preview_door" />
          ) : (
            <p className="nrd-note">Each row that needs a document names it, and only it. Adding your documents and reading the order with them is Pro.</p>
          )}
        </section>

        {gated ? null : (<>

        <section className="nrd-sec" id="nrd-03" aria-labelledby="nrd-03-h">
          <h2 id="nrd-03-h">03 · Appeal, rectify, or pay</h2>
          <div className="nrd-options">
            <div className="nrd-option"><span className="k">Appeal, s.107</span><p>{r.options.appeal.line}</p></div>
            <div className="nrd-option"><span className="k">Rectify, s.161</span><p>{r.options.rectify.line}</p></div>
            <div className="nrd-option"><span className="k">Pay</span><p>{r.options.pay.line}</p></div>
          </div>
          <div className="nrd-rows">
            <Code line="Pre-deposit to file the appeal" working={d.pre_deposit.working} chip={rupees(d.pre_deposit.amount)} />
            {d.reduced_penalty ? (
              <Code
                line={d.reduced_penalty.open ? `Pay in full by ${shortDate(d.reduced_penalty.until)}, penalty at 50%` : `The 50% penalty window closed on ${shortDate(d.reduced_penalty.until)}`}
                working={d.reduced_penalty.working}
                chip={lakh(d.reduced_penalty.amount)}
              />
            ) : null}
            <Code line={`Forum: ${d.forum.name}`} working={`${d.forum.working}. Next: ${d.forum.next}.`} chip="forum" />
          </div>
        </section>

        <section className="nrd-sec" id="nrd-04" aria-labelledby="nrd-04-h">
          <h2 id="nrd-04-h">04 · What Nira does not predict</h2>
          <p className="nrd-read">{r.not_predicted}</p>
        </section>

        <section className="nrd-sec" id="nrd-05" aria-labelledby="nrd-05-h">
          <h2 id="nrd-05-h">05 · What to do, by when</h2>
          <div className="nrd-todo">
            <div><span className="k">Appeal by</span><span className="v">{d.appeal_by ? shortDate(d.appeal_by) : "Not read"}</span>{d.working ? <span className="s">{d.working}</span> : null}</div>
            <div>
              <span className="k">Days left</span>
              <span className="v mono">{d.days_left === null ? "—" : late ? "Passed" : d.days_left}</span>
              {late && d.with_cause_until ? <span className="s">with cause, until {shortDate(d.with_cause_until)}</span> : null}
            </div>
          </div>
          <ol className="nrd-steps">
            {r.actions.steps.map((s, i) => (
              <li key={s.text}><span className="mono">{i + 1}</span><span>{s.text}</span><span className="mono nrd-by">{s.by}</span></li>
            ))}
          </ol>
          <div className="nrd-locked nrd-grounds">
            <div className="nrd-overlay static">
              <h3>Grounds of appeal, drafted.</h3>
              <p>Grounds of appeal are drafted in Nira Pro, written to the order's findings with the citations and documents from your workspace.</p>
              {pro ? <p className="nrd-note">Drafting the grounds is coming to Pro.</p> : <PaywallDoor className="ncf-btn primary npro-btn" metric="nira.appeal_reading" trigger={9} source="grounds">Draft the grounds</PaywallDoor>}
            </div>
          </div>
        </section>

        {r.exposure_hint ? <BeforeTheNotice hint={r.exposure_hint} /> : null}
        </>)}

        {r.limits.length ? (
          <section className="nrd-limits" aria-label="What this reading assumes">
            <p className="ncf-eyebrow">What this reading assumes</p>
            <ul>{r.limits.map((l) => <li key={l}>{l}</li>)}</ul>
            <p className="nrd-note">Computed on {shortDate(r.computed_on)} from the order's text. Research, not legal advice.</p>
          </section>
        ) : null}
        {level === "free" ? <CreditNote of="order" /> : null}
        {hydrate ? <ReadingComposer hydrate={hydrate} /> : null}
      </main>
    </div>
  )
}
